import React, { useEffect, useMemo, useState } from "react";
import api, { formatINR, formatApiErrorDetail } from "@/api/client";
import { toast } from "sonner";
import { Search, X, Eye, Truck, Package, CheckCircle2, ShieldX, Filter } from "lucide-react";

const STATUS_OPTIONS = [
  "placed", "confirmed", "packed", "shipped", "out_for_delivery",
  "delivered", "cancelled", "return_requested", "returned",
];
const STATUS_COLOR = {
  placed: "bg-blue-100 text-blue-700",
  confirmed: "bg-blue-100 text-blue-700",
  packed: "bg-indigo-100 text-indigo-700",
  shipped: "bg-purple-100 text-purple-700",
  out_for_delivery: "bg-amber-100 text-amber-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  return_requested: "bg-amber-100 text-amber-700",
  returned: "bg-amber-100 text-amber-700",
  refunded: "bg-green-100 text-green-700",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "placed", label: "Pending" },
  { key: "confirmed", label: "Confirmed" },
  { key: "packed", label: "Packed" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
];

const SellerOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [shipModal, setShipModal] = useState(null); // order object
  const [courier, setCourier] = useState("");
  const [trackingId, setTrackingId] = useState("");
  const [busy, setBusy] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders/me/seller");
      setOrders(data);
    } catch (e) {
      toast.error("Could not load orders");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (filter !== "all" && o.status !== filter) return false;
      if (q) {
        const needle = q.toLowerCase();
        const haystack = `${o.order_number} ${o.buyer_name || ""} ${(o.address?.phone || "")} ${(o.tracking_id || "")}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [orders, filter, q]);

  const updateStatus = async (order, status) => {
    try {
      await api.post(`/orders/${order.id}/status`, { status });
      toast.success(`${order.order_number} → ${status.replace(/_/g, " ")}`);
      fetch();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const openShip = (order) => {
    setShipModal(order);
    setCourier(order.courier_name || "");
    setTrackingId(order.tracking_id || "");
  };

  const confirmShip = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${shipModal.id}/status`, {
        status: "shipped",
        courier_name: courier || undefined,
        tracking_id: trackingId || undefined,
      });
      toast.success(`${shipModal.order_number} marked as shipped`);
      setShipModal(null); setCourier(""); setTrackingId("");
      fetch();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  const counts = useMemo(() => {
    const c = { total: orders.length, pending: 0, shipped: 0, delivered: 0, revenue: 0 };
    for (const o of orders) {
      if (o.status === "placed") c.pending++;
      if (o.status === "shipped" || o.status === "out_for_delivery") c.shipped++;
      if (o.status === "delivered") c.delivered++;
      if (!["cancelled", "refunded"].includes(o.status)) c.revenue += Number(o.total || 0);
    }
    return c;
  }, [orders]);

  return (
    <div data-testid="seller-orders-module">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Kpi color="bg-primary/10 text-primary" label="Orders" value={counts.total} icon={Package} />
        <Kpi color="bg-amber-100 text-amber-700" label="Pending" value={counts.pending} icon={Filter} />
        <Kpi color="bg-purple-100 text-purple-700" label="Shipped" value={counts.shipped} icon={Truck} />
        <Kpi color="bg-green-100 text-green-700" label="Revenue" value={formatINR(counts.revenue)} icon={CheckCircle2} />
      </div>

      {/* Filter tabs + search */}
      <div className="card-fah p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${filter === f.key ? "bg-primary text-white" : "bg-muted text-foreground hover:bg-muted/70"}`} data-testid={`seller-orders-filter-${f.key}`}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-lg px-3 py-1.5 ml-auto min-w-[200px]">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order #, customer, mobile…" className="flex-1 text-sm focus:outline-none bg-transparent" data-testid="seller-orders-search" />
            {q && <button onClick={() => setQ("")}><X className="w-4 h-4 text-muted-foreground" /></button>}
          </div>
        </div>
      </div>

      {/* Orders cards */}
      <div className="space-y-3" data-testid="seller-orders-list">
        {loading && <div className="card-fah p-8 text-center text-muted-foreground">Loading orders…</div>}
        {!loading && filtered.length === 0 && <div className="card-fah p-8 text-center text-muted-foreground">No orders in this view.</div>}
        {filtered.map((o) => {
          const myItems = o.items.filter(() => true); // already filtered to seller's items at API
          return (
            <div key={o.id} className="card-fah p-4" data-testid={`seller-order-${o.order_number}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-muted-foreground">Order #{o.order_number} · {new Date(o.created_at).toLocaleString("en-IN")}</div>
                  <div className="font-semibold mt-0.5">{o.address?.full_name || o.buyer_name} · {o.address?.phone}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{o.address?.line1}, {o.address?.city} {o.address?.pincode}</div>
                </div>
                <div className="text-right">
                  <span className={`text-[11px] font-bold uppercase px-2 py-1 rounded ${STATUS_COLOR[o.status] || "bg-muted text-foreground"}`}>{o.status.replace(/_/g, " ")}</span>
                  <div className="mt-1 font-bold">{formatINR(o.total)}</div>
                  <div className="text-[10px] uppercase text-muted-foreground">{o.payment_method} · {o.payment_status}</div>
                </div>
              </div>

              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {myItems.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 bg-muted/30 rounded-lg p-2">
                    <img src={it.image} alt="" className="w-10 h-10 rounded object-cover" />
                    <div className="flex-1 min-w-0 text-xs">
                      <div className="font-medium line-clamp-1">{it.name}</div>
                      <div className="text-muted-foreground">SKU {it.sku || "—"} · ×{it.qty} · {formatINR(it.price * it.qty)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {o.tracking_id && (
                <div className="mt-3 text-xs flex items-center gap-2 text-muted-foreground">
                  <Truck className="w-3.5 h-3.5" /> Tracking: <span className="font-mono font-semibold">{o.tracking_id}</span>{o.courier_name ? ` · ${o.courier_name}` : ""}
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2">
                {o.status === "placed" && (
                  <>
                    <ActionBtn onClick={() => updateStatus(o, "confirmed")} testid={`seller-accept-${o.order_number}`} color="bg-primary text-white"><CheckCircle2 className="w-4 h-4" /> Accept Order</ActionBtn>
                    <ActionBtn onClick={() => updateStatus(o, "cancelled")} testid={`seller-reject-${o.order_number}`} color="bg-white border border-destructive/40 text-destructive hover:bg-destructive/10"><ShieldX className="w-4 h-4" /> Reject</ActionBtn>
                  </>
                )}
                {o.status === "confirmed" && (
                  <ActionBtn onClick={() => updateStatus(o, "packed")} testid={`seller-pack-${o.order_number}`} color="bg-indigo-600 text-white"><Package className="w-4 h-4" /> Mark as Packed</ActionBtn>
                )}
                {o.status === "packed" && (
                  <ActionBtn onClick={() => openShip(o)} testid={`seller-ship-${o.order_number}`} color="bg-purple-600 text-white"><Truck className="w-4 h-4" /> Mark as Shipped</ActionBtn>
                )}
                {o.status === "shipped" && (
                  <>
                    <ActionBtn onClick={() => updateStatus(o, "out_for_delivery")} testid={`seller-ofd-${o.order_number}`} color="bg-amber-500 text-white"><Truck className="w-4 h-4" /> Out for Delivery</ActionBtn>
                    <ActionBtn onClick={() => updateStatus(o, "delivered")} testid={`seller-delivered-${o.order_number}`} color="bg-green-600 text-white"><CheckCircle2 className="w-4 h-4" /> Mark Delivered</ActionBtn>
                  </>
                )}
                {o.status === "out_for_delivery" && (
                  <ActionBtn onClick={() => updateStatus(o, "delivered")} testid={`seller-delivered-${o.order_number}`} color="bg-green-600 text-white"><CheckCircle2 className="w-4 h-4" /> Mark Delivered</ActionBtn>
                )}
                <a href={`/order/${o.id}`} target="_blank" rel="noreferrer" className="text-xs font-semibold px-3 py-2 rounded-lg bg-muted text-foreground hover:bg-muted/70 inline-flex items-center gap-1.5 ml-auto" data-testid={`seller-view-${o.order_number}`}>
                  <Eye className="w-3.5 h-3.5" /> View
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* Ship modal */}
      {shipModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShipModal(null)} data-testid="ship-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold">Ship Order — {shipModal.order_number}</h2>
              <button onClick={() => setShipModal(null)} className="p-1.5 hover:bg-muted rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Courier Name</label>
                <input value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="Delhivery, Bluedart, Indiapost…" className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-white" data-testid="ship-courier" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tracking ID</label>
                <input value={trackingId} onChange={(e) => setTrackingId(e.target.value)} placeholder="Leave blank to auto-generate" className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-white font-mono" data-testid="ship-tracking" />
              </div>
              <p className="text-xs text-muted-foreground">An SMS + email notification will be sent to the customer (currently <span className="font-mono">MOCKED</span> — logs only).</p>
              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button onClick={() => setShipModal(null)} className="text-sm font-semibold px-4 py-2 rounded-lg hover:bg-muted">Cancel</button>
                <button disabled={busy} onClick={confirmShip} className="bg-primary text-white font-bold text-sm px-5 py-2 rounded-lg disabled:opacity-50" data-testid="ship-confirm">
                  {busy ? "Updating…" : "Confirm Shipment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Kpi = ({ icon: Icon, color, label, value }) => (
  <div className="card-fah p-3 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-bold">{value}</div>
    </div>
  </div>
);

const ActionBtn = ({ children, onClick, testid, color }) => (
  <button onClick={onClick} className={`text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5 ${color}`} data-testid={testid}>{children}</button>
);

export default SellerOrders;
